/**
 * `tour/SpotlightFrame.tsx` — the spotlight cut-out and card, shared by the
 * first-visit tour (`SpotlightTour.tsx`) and the demo guide (#2824).
 *
 * A fixed layer dims the page except a rounded cut-out around the current
 * element; a card beside it holds whatever the caller renders. The cut-out is
 * one rounded box with a page-sized shadow in the dim colour. It sticks to the
 * element with no animation, and the frame stays hidden until the element
 * holds still, so the cut-out never lands in one place and then moves. A transparent layer under it keeps the page from
 * taking clicks. With `interactive` that layer leaves the cut-out open, so
 * the visitor can press the real control. Escape calls `onEscape`; focus
 * stays in the card. No external library.
 */
import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { flushSync } from "react-dom";

import { cn } from "../../lib/cn";

/** Space between the anchor and the cut-out's edge. */
const HOLE_PAD = 6;
/** Space between the cut-out and the card. */
const CARD_GAP = 12;
/**
 * A stop's lit element changed after it landed (#2962): the card stays where
 * it is when the new ring is this near and nothing covers it.
 */
const KEEP_NEAR_PX = 160;
/** Gutter kept clear at the viewport's edges. */
const EDGE = 16;
// Wide enough for "Skip tour", "Autoplay", "Back" and "Explore on my own" in one row.
const CARD_MAX_W = 352;
/** Below this width the card sits centred at the bottom of the screen. */
const PHONE_MAX_W = 640;
/** The element's box must stay the same this long (two frames or more) before the stop lands. */
export const STABLE_MS = 50;
/** A stop lands after this long even if its element keeps moving. */
export const STABLE_MAX_MS = 1200;
/** An element off screen (a panel still sliding in) gets this long before the stop lands anyway. */
export const OFF_SCREEN_MAX_MS = 3000;
/** The frame reads the page this often, as well as on frames and events (frames stop in a hidden tab). */
const TICK_MS = 100;

/**
 * The bottom edge of the sticky or fixed bar at the top of the page, or 0. A
 * target inside that bar (a nav item) gets 0, so it lights as before.
 */
function topChromeBottom(target: Element): number {
  let bottom = 0;
  for (const bar of Array.from(document.querySelectorAll<HTMLElement>("header"))) {
    if (bar.contains(target)) return 0;
    const pos = window.getComputedStyle(bar).position;
    if (pos !== "sticky" && pos !== "fixed") continue;
    const r = bar.getBoundingClientRect();
    if (r.top <= 1 && r.height > 0 && r.bottom > bottom) bottom = r.bottom;
  }
  return bottom;
}

/** CSS transitions on these properties move or resize an element. */
const GEOMETRY =
  /^(all|transform|translate|scale|rotate|top|left|right|bottom|inset|width|height|max-height|max-width|margin)/;

/**
 * True while a finite CSS animation or a moving transition runs on the element
 * or a parent (a panel slides in, a dialog grows). Frame-rate independent: a
 * slow or throttled page still reads as moving until the animation ends.
 */
function animating(el: Element | null): boolean {
  if (!el || typeof document.getAnimations !== "function") return false;
  for (const a of document.getAnimations()) {
    if (a.playState !== "running") continue;
    const effect = a.effect as KeyframeEffect | null;
    const target = effect?.target;
    if (!(target instanceof Element) || !(target === el || target.contains(el))) continue;
    if (effect?.getComputedTiming().endTime === Number.POSITIVE_INFINITY) continue;
    const prop = (a as Animation & { transitionProperty?: string }).transitionProperty;
    if (prop !== undefined && !GEOMETRY.test(prop)) continue;
    return true;
  }
  return false;
}

/** A moment on two clocks. Test fakes move one or the other; a browser moves both. */
interface Moment {
  p: number;
  d: number;
}
function moment(): Moment {
  return { p: performance.now(), d: Date.now() };
}
function since(m: Moment, now: Moment): number {
  return Math.max(now.p - m.p, now.d - m.d);
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface SpotlightFrameProps {
  /** `data-testid` prefix: root, `-hole` and `-card`. */
  testIdPrefix?: string;
  /** Changes with each stop: re-focuses the card and keys it. */
  stepKey: string;
  /** The element to cut out, read again every frame. Null: no cut-out. */
  getElement: () => HTMLElement | null;
  /** No cut-out: the card sits in the middle of a dimmed page. */
  centered?: boolean;
  /** The cut-out passes clicks to the page. */
  interactive?: boolean;
  /**
   * The page is not dimmed and takes every click (the demo guide, #2824): the
   * lit element keeps a ring and a soft glow, and the card stays. Default off.
   */
  passThrough?: boolean;
  /**
   * A panel the card and the cut-out stay clear of (the demo phone, #2824):
   * the cut-out stops at its edge and the card sits on the other side.
   */
  getClearOf?: (() => HTMLElement | null) | undefined;
  /**
   * A panel only the card keeps clear of (#2824: the station drawer behind the
   * review). The card sits in the free strip beside it. The cut-out is never
   * trimmed for it.
   */
  getAvoid?: (() => HTMLElement | null) | undefined;
  /**
   * The next stop is not ready (its page or element is on the way, #2824). The
   * card of the last stop stays, still and unpressable, and the new text
   * replaces it only once the new place holds still. With no last stop there is
   * nothing to show yet.
   */
  hold?: boolean;
  /**
   * Called once each time a stop lands: its ring (when it has an element) and
   * its card show in that same frame. `el` is the lit element, or null.
   */
  onShown?: ((stepKey: string, el: HTMLElement | null) => void) | undefined;
  /**
   * Called when the ring moves or grows after its stop landed (the page
   * reflowed under it, #2824). The card stays; a caller that follows the ring
   * (the Autoplay pointer) follows it again. `el` is the lit element.
   */
  onRingMove?: ((stepKey: string, el: HTMLElement | null) => void) | undefined;
  onEscape: () => void;
  /** Focused on every stop. Without it the card's first button is. */
  primaryRef?: RefObject<HTMLButtonElement | null>;
  /**
   * The card's surface. "guide" (the demo guide, #2824): a tinted, raised card
   * with an accent border, ring and shadow, so it stands out from the page and
   * from the page's own cards. Default "plain": the page's card surface.
   */
  surface?: "plain" | "guide";
  children: (ids: { titleId: string; bodyId: string }) => ReactNode;
}

/**
 * One stop goes through these states (#2824):
 *
 * 1. Wait. `hold` is on, or the element is missing, moving (its box changed in
 *    the last `STABLE_MS`, or a CSS animation runs on it or a parent), or off
 *    screen. An element that holds still off screen is scrolled to the centre
 *    once, and the wait goes on until the scroll ends. Meanwhile the last
 *    stop's card stays where it was, with no ring. With no last stop nothing
 *    shows.
 * 2. Landed. The ring and the new card show in the same frame. The card's
 *    place is fixed for the stop: the ring follows the element (it may grow or
 *    move), the card does not. Only a new lit element, or the phone opening
 *    over the card, places the card again. `onShown` runs in that frame.
 *
 * After `STABLE_MAX_MS` of waiting (hold off) the stop lands as it is.
 */
export function SpotlightFrame({
  testIdPrefix = "spotlight-tour",
  stepKey,
  getElement,
  centered = false,
  interactive = false,
  passThrough = false,
  getClearOf,
  getAvoid,
  hold = false,
  onShown,
  onRingMove,
  onEscape,
  primaryRef,
  surface = "plain",
  children,
}: SpotlightFrameProps) {
  const titleId = useId();
  const bodyId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const [hole, setHole] = useState<Box | null>(null);
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  const [clear, setClear] = useState<Box | null>(null);
  const [avoid, setAvoid] = useState<Box | null>(null);
  const [cardH, setCardH] = useState(180);
  const landKey = `${stepKey}|${centered}`;
  // The stop that landed, and the element it lit. A new stop reads "not landed"
  // in the same render it arrives in.
  const [landed, setLanded] = useState<{ key: string; el: HTMLElement | null } | null>(null);
  const live = landed?.key === landKey && !hold;
  const liveRef = useRef(live);
  liveRef.current = live;
  const landedRef = useRef(landed);
  landedRef.current = landed;
  const waitRef = useRef({
    key: "",
    start: moment(),
    box: "",
    changed: moment(),
    reads: 0,
    scrolled: false,
  });
  // The card's place, fixed once the stop landed.
  const frozenRef = useRef<{ key: string; el: HTMLElement | null; style: CSSProperties } | null>(
    null,
  );
  const lastShownRef = useRef<{
    key: string;
    stepKey: string;
    node: ReactNode;
    style: CSSProperties;
  } | null>(null);
  const shownRef = useRef<string | null>(null);
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  const onRingMoveRef = useRef(onRingMove);
  onRingMoveRef.current = onRingMove;
  // The ring as it was drawn when the stop landed, then as last reported.
  const landedHoleRef = useRef<Box | null>(null);

  /** Reads the element and the panel, updates the ring. Returns the element's own rect. */
  const measure = useCallback((): { el: HTMLElement | null; rect: DOMRect | null } => {
    const el = centered ? null : getElement();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setViewport((v) => (v.w === vw && v.h === vh ? v : { w: vw, h: vh }));
    const panel = getClearOf?.() ?? null;
    // The card keeps clear of the panel even when the lit element is the panel
    // itself (the phone step). The cut-out is trimmed only for another element.
    const panelRect = panel ? panel.getBoundingClientRect() : null;
    const nextClear: Box | null =
      panelRect && panelRect.width > 0 && panelRect.height > 0
        ? {
            top: panelRect.top,
            left: panelRect.left,
            width: panelRect.width,
            height: panelRect.height,
          }
        : null;
    const pr = panel && panel !== el && !el?.contains(panel) ? panelRect : null;
    setClear((prev) =>
      prev === nextClear || (prev && nextClear && sameBox(prev, nextClear)) ? prev : nextClear,
    );
    const avoidRect = getAvoid?.()?.getBoundingClientRect() ?? null;
    const nextAvoid: Box | null =
      avoidRect && avoidRect.width > 0 && avoidRect.height > 0
        ? {
            top: avoidRect.top,
            left: avoidRect.left,
            width: avoidRect.width,
            height: avoidRect.height,
          }
        : null;
    setAvoid((prev) =>
      prev === nextAvoid || (prev && nextAvoid && sameBox(prev, nextAvoid)) ? prev : nextAvoid,
    );
    // A lit element that left the page for a moment keeps its last ring: the
    // ring never drops out and comes back inside one stop.
    if (!el) return { el: null, rect: null };
    const r = el.getBoundingClientRect();
    // Never draw the hole over the sticky top bar (#2824): cut it at the bar.
    const inset = topChromeBottom(el);
    let top = Math.max(r.top - HOLE_PAD, inset);
    let bottom = r.bottom + HOLE_PAD;
    let left = r.left - HOLE_PAD;
    let width = r.width + HOLE_PAD * 2;
    // The phone lies over the page: the cut-out stops at the phone's edge.
    if (pr && nextClear && pr.width > 0 && r.bottom > pr.top && r.top < pr.bottom) {
      const phoneOnRight = pr.left + pr.width / 2 > window.innerWidth / 2;
      if (phoneOnRight && left + width > pr.left - HOLE_PAD && left < pr.left) {
        width = Math.max(40, pr.left - HOLE_PAD - left);
      } else if (!phoneOnRight && left < pr.right && left + width > pr.right) {
        width = left + width - (pr.right + HOLE_PAD);
        left = pr.right + HOLE_PAD;
      }
    }
    // An element larger than the window: the ring stays on screen, around the
    // part that shows (#2824).
    const clipTop = Math.max(top, 2);
    const clipBottom = Math.min(bottom, vh - 2);
    const clipLeft = Math.max(left, 2);
    const clipRight = Math.min(left + width, vw - 2);
    if (clipBottom > clipTop && clipRight > clipLeft) {
      top = clipTop;
      left = clipLeft;
      width = clipRight - clipLeft;
      bottom = clipBottom;
    }
    const next = { top, left, width, height: Math.max(0, bottom - top) };
    setHole((prev) => (prev && sameBox(prev, next) ? prev : next));
    return { el, rect: r };
  }, [getElement, centered, getClearOf, getAvoid]);

  // One loop for the life of the frame: every animation frame and every
  // TICK_MS, read the page. While the stop waits, decide when it lands.
  useEffect(() => {
    let raf = 0;
    let stopped = false;
    /** True when the landed card can stay for a new lit element: near it, over nothing. */
    const keepCard = (el: HTMLElement): boolean => {
      const card = cardRef.current?.getBoundingClientRect();
      if (!card || card.width === 0) return false;
      const r = el.getBoundingClientRect();
      const ring = new DOMRect(
        r.left - HOLE_PAD,
        r.top - HOLE_PAD,
        r.width + HOLE_PAD * 2,
        r.height + HOLE_PAD * 2,
      );
      if (overlaps(card, ring)) return false;
      const phone = getClearOf?.();
      const drawer = getAvoid?.();
      for (const panel of [phone && !phone.contains(el) ? phone : null, drawer]) {
        const p = panel?.getBoundingClientRect();
        if (p && p.width > 0 && p.height > 0 && overlaps(card, p)) return false;
      }
      const dx = Math.max(0, ring.left - card.right, card.left - ring.right);
      const dy = Math.max(0, ring.top - card.bottom, card.top - ring.bottom);
      return Math.hypot(dx, dy) <= KEEP_NEAR_PX;
    };
    const tick = () => {
      if (stopped) return;
      const { el, rect } = measure();
      const now = moment();
      const done = landedRef.current;
      if (done?.key === landKey) {
        // Landed: a new lit element, or a panel now over the card, places the
        // card again. Nothing else moves it.
        const frozen = frozenRef.current;
        if (el && el !== done.el && frozen?.key === landKey && keepCard(el)) {
          // Nothing moves inside a stop (#2962): the ring goes to the new
          // element, the card stays, since it is near and clear of it.
          flushSync(() => {
            measure();
            setLanded({ key: landKey, el });
          });
        } else if (el && el !== done.el) {
          frozenRef.current = null;
          // The new ring goes in the same render, so the card is placed
          // against it and not against the last element (#2824).
          flushSync(() => {
            measure();
            setLanded({ key: landKey, el });
          });
        } else if (frozen && frozen.key === landKey && cardRef.current) {
          const panel = getClearOf?.();
          const p = panel?.getBoundingClientRect();
          const c = cardRef.current.getBoundingClientRect();
          const next = computedRef.current;
          const moved = next.top !== frozen.style.top || next.left !== frozen.style.left;
          if (p && p.width > 0 && overlaps(c, p) && moved) {
            frozenRef.current = null;
            flushSync(() => setLanded({ key: landKey, el: done.el }));
          }
        }
        return;
      }
      const w = waitRef.current;
      if (w.key !== landKey || hold) {
        // A new stop with no element yet never shows the last stop's ring.
        if (w.key !== landKey && !el) setHole(null);
        waitRef.current = {
          key: landKey,
          start: now,
          box: "",
          changed: now,
          reads: 0,
          scrolled: false,
        };
        return;
      }
      // The landing renders at once, in this frame: the ring and the card
      // show together and the caller hears of it before anything else runs.
      // The read runs again inside the landing render, so the card is placed
      // against the drawer and panel as they are now (#2824).
      const land = () => {
        frozenRef.current = null;
        flushSync(() => {
          measure();
          setLanded({ key: landKey, el });
        });
      };
      if (centered) {
        land();
        return;
      }
      const waited = since(w.start, now);
      if (waited >= OFF_SCREEN_MAX_MS || (waited >= STABLE_MAX_MS && !outside(rect))) {
        land();
        return;
      }
      if (!el || !rect) return;
      const panel = getClearOf?.() ?? null;
      const p = panel?.getBoundingClientRect();
      const box = `${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.width)},${Math.round(rect.height)}|${p ? `${Math.round(p.left)},${Math.round(p.top)},${Math.round(p.width)}` : ""}`;
      w.reads += 1;
      if (box !== w.box) {
        w.box = box;
        w.changed = now;
        return;
      }
      if (since(w.changed, now) < STABLE_MS || w.reads < 2) return;
      if (animating(el) || animating(panel)) return;
      const off = outside(rect) || rect.top < topChromeBottom(el);
      if (off && !w.scrolled && typeof el.scrollIntoView === "function") {
        // Off screen and still: bring it to the middle, then wait for the scroll to end.
        w.scrolled = true;
        w.changed = now;
        el.scrollIntoView({ block: "center", inline: "center" });
        return;
      }
      land();
    };
    // While the stop waits: a read every frame. Once it landed: a read on each
    // scroll and resize (one per frame), and every TICK_MS for what moves
    // without an event (a panel sliding in). No frame loop then.
    const loop = () => {
      raf = 0;
      tick();
      if (landedRef.current?.key !== landKey) raf = window.requestAnimationFrame(loop);
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(loop);
    };
    // The first read waits for the next frame or tick: a landing renders at
    // once, which an effect must not do.
    raf = window.requestAnimationFrame(loop);
    const timer = window.setInterval(tick, TICK_MS);
    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, { capture: true, passive: true });
    return () => {
      stopped = true;
      window.cancelAnimationFrame(raf);
      window.clearInterval(timer);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, { capture: true });
    };
  }, [measure, landKey, hold, centered, getClearOf, getAvoid]);

  // Focus the primary button (or the first one) on every stop, once it has
  // landed: a hidden card cannot take focus.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-focus when the stop changes
  useEffect(() => {
    if (!live) return;
    if (primaryRef?.current) {
      primaryRef.current.focus();
      return;
    }
    cardRef.current?.querySelector<HTMLElement>("button:not([disabled])")?.focus();
  }, [stepKey, live]);

  // While the next stop waits, the old card stays on screen but is dead: its
  // focused button must not take a second key press (#2824). The card is keyed
  // per stop, so a new card starts live and takes focus above.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the stop changes
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const stale = !live && lastShownRef.current !== null;
    card.toggleAttribute("inert", stale);
    if (stale) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && card.contains(active)) active.blur();
    }
  }, [live, stepKey]);

  // Escape closes, wherever focus is.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onEscape();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onEscape]);

  // Basic focus trap: Tab cycles within the card. An interactive stop lets
  // Tab reach the page, because the visitor is meant to use it.
  const onCardKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (interactive || passThrough || e.key !== "Tab" || !cardRef.current) return;
    const items = Array.from(
      cardRef.current.querySelectorAll<HTMLElement>("button:not([disabled])"),
    );
    if (items.length === 0) return;
    const first = items[0];
    const lastItem = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !cardRef.current.contains(active))) {
      e.preventDefault();
      lastItem?.focus();
    } else if (!e.shiftKey && (active === lastItem || !cardRef.current.contains(active))) {
      e.preventDefault();
      first?.focus();
    }
  };

  const phone = viewport.w < PHONE_MAX_W;
  // The free strip of the page beside the phone, when there is one.
  let freeLeft = 0;
  let freeRight = viewport.w;
  for (const panel of [clear, avoid]) {
    if (!panel || phone) continue;
    if (panel.left + panel.width / 2 > viewport.w / 2) {
      freeRight = Math.min(freeRight, Math.max(0, panel.left));
    } else freeLeft = Math.max(freeLeft, panel.left + panel.width);
  }
  const cardW = Math.min(CARD_MAX_W, Math.max(220, freeRight - freeLeft - EDGE * 2));
  const cardHole = centered ? null : hole;
  let cardStyle: CSSProperties;
  if (phone || (!cardHole && !centered)) {
    cardStyle = { left: EDGE, right: EDGE, bottom: EDGE, marginInline: "auto" };
  } else if (!cardHole) {
    cardStyle = {
      top: Math.max(EDGE, (viewport.h - cardH) / 2),
      left: Math.max(EDGE, freeLeft + (freeRight - freeLeft - cardW) / 2),
    };
  } else {
    const below = cardHole.top + cardHole.height + CARD_GAP;
    const above = cardHole.top - CARD_GAP - cardH;
    const top =
      below + cardH <= viewport.h - EDGE
        ? below
        : above >= EDGE
          ? above
          : Math.max(EDGE, viewport.h - cardH - EDGE);
    const left = clamp(
      cardHole.left + cardHole.width / 2 - cardW / 2,
      freeLeft + EDGE,
      freeRight - cardW - EDGE,
    );
    cardStyle = { top, left };
    // The lit area is the phone (or reaches it): the card sits in the free strip
    // beside it, level with the lit area, never over it.
    if (
      clear &&
      !phone &&
      cardHole.left < clear.left + clear.width &&
      cardHole.left + cardHole.width > clear.left
    ) {
      cardStyle = {
        top: clamp(cardHole.top + cardHole.height / 2 - cardH / 2, EDGE, viewport.h - cardH - EDGE),
        left,
      };
    }
  }
  const computedStyle = { ...cardStyle, width: cardW, maxWidth: CARD_MAX_W } as CSSProperties;
  const frozen = frozenRef.current;
  const liveStyle = live && frozen?.key === landKey ? frozen.style : computedStyle;
  const computedRef = useRef(computedStyle);
  computedRef.current = computedStyle;

  // The landing frame: read the new card's real height before it paints, place
  // it once, and fix that place for the stop. Then tell the caller.
  useLayoutEffect(() => {
    if (!live) return;
    if (frozenRef.current?.key === landKey) return;
    const h = cardRef.current?.offsetHeight;
    if (h && h !== cardH) {
      setCardH(h);
      return;
    }
    const el = landedRef.current?.el ?? null;
    frozenRef.current = { key: landKey, el, style: computedRef.current };
    landedHoleRef.current = hole;
    const shownKey = `${landKey}|${shownCount(el)}`;
    if (shownRef.current !== shownKey) {
      shownRef.current = shownKey;
      onShownRef.current?.(stepKey, centered ? null : el);
    }
  });

  // The ring moved after the stop landed: tell the caller (the card stays).
  useEffect(() => {
    if (!live || centered || !hole) return;
    const base = landedHoleRef.current;
    if (!base || frozenRef.current?.key !== landKey || sameBox(base, hole)) return;
    landedHoleRef.current = hole;
    onRingMoveRef.current?.(stepKey, landedRef.current?.el ?? null);
  }, [hole, live, centered, landKey, stepKey]);

  // The last landed card stays while the next stop waits, so the card is never
  // blank between stops.
  const node = children({ titleId, bodyId });
  if (live) lastShownRef.current = { key: landKey, stepKey, node, style: liveStyle };
  const last = lastShownRef.current;
  if (!live && hold && !last) return null;
  const stale = !live && last !== null;
  const shownNode = stale && last ? last.node : node;
  const shownStyle = stale && last ? last.style : liveStyle;
  const shownStep = stale && last ? last.stepKey : stepKey;
  // The ring and the card show in the same frame: the ring is on only beside
  // the current stop's own landed card.
  const ringOn = Boolean(hole) && live && !centered;

  return (
    <div data-testid={testIdPrefix} data-step={stepKey} data-settled={live ? "true" : "false"}>
      {/* Keeps the page from taking clicks while the card is up (not in pass-through). */}
      {passThrough ? null : interactive && hole && ringOn ? (
        <Blockers hole={hole} />
      ) : (
        <div className="fixed inset-0 z-[60]" aria-hidden="true" />
      )}
      {/* Until the stop lands, the page stays dimmed in full and the ring and
          card wait, so nothing lands and then moves. */}
      {hole && ringOn ? (
        <div
          aria-hidden="true"
          data-testid={`${testIdPrefix}-hole`}
          className="pointer-events-none fixed z-[60] rounded-lg"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            // The accent ring, then the dim layer as one page-sized shadow.
            // Pass-through: the ring and a soft glow only, no dim.
            boxShadow: passThrough
              ? "0 0 0 2px rgb(var(--color-accent)), 0 0 0 5px rgb(var(--color-accent) / 0.35), 0 0 22px 4px rgb(var(--color-accent) / 0.55)"
              : `0 0 0 2px rgb(var(--color-accent)), 0 0 0 200vmax ${dimColour()}`,
          }}
        />
      ) : passThrough ? null : (
        <SpotlightDim />
      )}
      <div
        ref={cardRef}
        // biome-ignore lint/a11y/useSemanticElements: a positioned card, not a native <dialog> (its top-layer modal would hide the spotlight); role + aria-modal + the focus trap keep the semantics.
        role="dialog"
        aria-modal={passThrough ? "false" : "true"}
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={onCardKeyDown}
        data-testid={`${testIdPrefix}-card`}
        // No fade in: the card is whole in the frame its ring shows.
        className={cn(
          "fixed z-[61] rounded-lg p-4 text-fg",
          surface === "guide" ? GUIDE_SURFACE : "border border-border bg-bg-elevated shadow-lg",
          stale ? "pointer-events-none" : live ? "" : "invisible",
        )}
        data-surface={surface}
        data-stale={stale ? "true" : undefined}
        data-pending={live || stale ? undefined : "true"}
        aria-hidden={live ? undefined : "true"}
        style={shownStyle}
        key={shownStep}
      >
        {shownNode}
      </div>
    </div>
  );
}

/**
 * The demo guide's card surface (#2824): the accent's soft tint over the card
 * colour, a 2 px accent border, a soft accent ring and a deep shadow. Tokens
 * only, so it follows every theme. Text on it stays WCAG AA in both themes.
 */
export const GUIDE_SURFACE =
  "border-2 border-accent/60 bg-bg-elevated bg-[linear-gradient(rgb(var(--color-accent-soft)/0.75),rgb(var(--color-accent-soft)/0.75))] shadow-2xl ring-4 ring-accent/15";

/** Tells one landing of a stop from the next: a new lit element is a new landing. */
const elementIds = new WeakMap<HTMLElement, number>();
let nextElementId = 1;
function shownCount(el: HTMLElement | null): number {
  if (!el) return 0;
  let id = elementIds.get(el);
  if (!id) {
    id = nextElementId++;
    elementIds.set(el, id);
  }
  return id;
}

/** True when part of the box lies outside the window. */
function outside(r: DOMRect | null): boolean {
  if (!r) return false;
  return r.bottom > window.innerHeight || r.right > window.innerWidth || r.top < 0 || r.left < 0;
}

function overlaps(a: DOMRect, b: DOMRect): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/** Four panels around the cut-out: the page takes clicks only inside it. */
function Blockers({ hole }: { hole: Box }) {
  const right = hole.left + hole.width;
  const bottom = hole.top + hole.height;
  const panel = "fixed z-[60]";
  return (
    <div aria-hidden="true" data-testid="spotlight-blockers">
      <div className={panel} style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) }} />
      <div className={panel} style={{ top: bottom, left: 0, right: 0, bottom: 0 }} />
      <div
        className={panel}
        style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height }}
      />
      <div
        className={panel}
        style={{ top: hole.top, left: right, right: 0, height: hole.height }}
      />
    </div>
  );
}

function sameBox(a: Box, b: Box): boolean {
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** ~55% of the page's dark colour: the text colour on a light theme, the
 *  page colour on a dark one. */
/** The page dimmed in full: while a stop waits for its element. */
export function SpotlightDim() {
  return (
    <div
      aria-hidden="true"
      data-testid="spotlight-dim"
      className="pointer-events-none fixed inset-0 z-[60]"
      style={{ background: dimColour() }}
    />
  );
}

function dimColour(): string {
  let dark = false;
  try {
    dark = getComputedStyle(document.documentElement).colorScheme.includes("dark");
  } catch {
    dark = false;
  }
  return dark ? "rgb(var(--color-bg) / 0.7)" : "rgb(var(--color-fg) / 0.55)";
}
